// src/constants/index.ts
// Single source of truth for magic strings used across the application.
// Import from here instead of hardcoding strings in components.

// ─── User roles ───────────────────────────────────────────────────────────────
export const USER_ROLES = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  CLIENT: 'Client',
  USER: 'User',
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// ─── Compliance statuses ─────────────────────────────────────────────────────
export const COMPLIANCE_STATUS = {
  COMPLETED: 'Completed',
  PENDING: 'Pending',
  OVERDUE: 'Overdue',
} as const;

export type ComplianceStatus = (typeof COMPLIANCE_STATUS)[keyof typeof COMPLIANCE_STATUS];

// ─── Maintenance statuses ─────────────────────────────────────────────────────
export const MAINTENANCE_STATUS = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
} as const;

export type MaintenanceStatus = (typeof MAINTENANCE_STATUS)[keyof typeof MAINTENANCE_STATUS];

// ─── Maintenance types ────────────────────────────────────────────────────────
export const MAINTENANCE_TYPE = {
  PREVENTIVE: 'Preventive',
  CORRECTIVE: 'Corrective',
} as const;

export type MaintenanceType = (typeof MAINTENANCE_TYPE)[keyof typeof MAINTENANCE_TYPE];

// ─── Secure storage keys ──────────────────────────────────────────────────────
// All reads/writes to secureLocalStorage must use these keys.
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
} as const;

// ─── Pagination defaults ──────────────────────────────────────────────────────
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  // Cap for "fetch everything" requests (e.g. archive documents). Overridable
  // per environment via REACT_APP_FETCH_ALL_LIMIT.
  FETCH_ALL_LIMIT: Number(process.env.REACT_APP_FETCH_ALL_LIMIT) || 1000000,
} as const;

// ─── API endpoints ────────────────────────────────────────────────────────────
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REFRESH: '/auth/refreshtoken',
    RESET_PASSWORD: '/auth/resetpassword',
    FORGOT_PASSWORD: '/auth/forgotpassword',
  },
} as const;
